import axios, {
  AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { API_URL } from '../utils/constants';
import { getAccessToken, removeAccessToken } from '../utils/Storage';
import type { ApiEnvelope } from '../models';

/** Matches backend `RET_SUCCESS` — business success inside HTTP 200. */
export const RET_SUCCESS = 0;

const isDev = process.env.NODE_ENV !== 'production';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

function isApiEnvelope(body: unknown): body is ApiEnvelope<unknown> {
  return (
    !!body &&
    typeof body === 'object' &&
    'code' in body &&
    'msg' in body &&
    'data' in body &&
    typeof (body as ApiEnvelope<unknown>).code === 'number' &&
    typeof (body as ApiEnvelope<unknown>).msg === 'string'
  );
}

function requestLabel(config?: InternalAxiosRequestConfig): string {
  const method = (config?.method ?? 'GET').toUpperCase();
  const url = config?.url ?? '(unknown)';
  return `${method} ${url}`;
}

function logApi(
  level: 'debug' | 'warn' | 'error',
  message: string,
  details?: Record<string, unknown>
): void {
  const payload = details ? { ...details } : undefined;
  if (level === 'debug') {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.debug(`[API] ${message}`, payload ?? '');
    }
    return;
  }
  if (level === 'warn') {
    console.warn(`[API] ${message}`, payload ?? '');
    return;
  }
  console.error(`[API] ${message}`, payload ?? '');
}

function rejectApiEnvelope(
  response: AxiosResponse,
  body: ApiEnvelope<unknown>
): Promise<never> {
  logApi('warn', `Business error: ${requestLabel(response.config)}`, {
    code: body.code,
    msg: body.msg,
    data: body.data,
    httpStatus: response.status,
  });

  return Promise.reject(
    new AxiosError(
      body.msg || 'Request failed',
      AxiosError.ERR_BAD_RESPONSE,
      response.config,
      response.request,
      {
        ...response,
        // Keep full envelope so callers can read `code` / field errors in `data`
        data: body,
      }
    )
  );
}

function handleUnauthorized(): void {
  removeAccessToken();
  localStorage.removeItem('user');

  if (!window.location.pathname.includes('/login')) {
    console.log('handleUnauthorized, redirecting to login from:', window.location.pathname);

    window.location.href = '/login';
  }
}

/**
 * Read a human-readable message from a rejected API call.
 * Prefers ApiEnvelope `msg`, then common DRF / legacy error shapes.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = 'Request failed'
): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as {
    message?: string;
    response?: {
      data?: {
        code?: number;
        msg?: string;
        message?: string;
        error?: string;
        errors?: Record<string, string | string[]>;
        [key: string]: unknown;
      };
    };
  };

  const data = err.response?.data;

  if (data && typeof data === 'object') {
    if (typeof data.msg === 'string' && data.msg.trim()) {
      return data.msg;
    }
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error;
    }
    if (data.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (typeof first === 'string' && first.trim()) {
        return first;
      }
    }

    // DRF field errors: { email: ["..."], name: ["..."] } — not an envelope
    if (!('code' in data && 'msg' in data)) {
      for (const value of Object.values(data)) {
        if (typeof value === 'string' && value.trim()) {
          return value;
        }
        if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) {
          return value[0];
        }
      }
    }
  }

  if (
    typeof err.message === 'string' &&
    err.message.trim() &&
    !err.message.startsWith('Request failed with status code')
  ) {
    return err.message;
  }

  return fallback;
}

// Attach auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    logApi('debug', `→ ${requestLabel(config)}`);
    return config;
  },
  (error) => {
    logApi('error', 'Request setup failed', {
      message: error instanceof Error ? error.message : String(error),
    });
    return Promise.reject(error);
  }
);

/**
 * Unified response interceptor:
 * - Unwraps `{ code, msg, data }` so callers receive `data` on success
 * - Rejects business failures (`code !== 0`) even when HTTP status is 200
 * - Normalizes HTTP / network errors and logs them once
 */
api.interceptors.response.use(
  (response) => {
    const body = response.data;

    if (!isApiEnvelope(body)) {
      logApi('debug', `← ${requestLabel(response.config)} (raw)`, {
        httpStatus: response.status,
      });
      return response;
    }

    if (body.code !== RET_SUCCESS) {
      return rejectApiEnvelope(response, body);
    }

    logApi('debug', `← ${requestLabel(response.config)} ok`, {
      httpStatus: response.status,
    });

    response.data = body.data;
    return response;
  },
  (error: unknown) => {
    if (axios.isCancel(error)) {
      const config = (error as AxiosError).config;
      logApi('debug', `Cancelled: ${requestLabel(config)}`);
      return Promise.reject(error);
    }

    if (!axios.isAxiosError(error)) {
      logApi('error', 'Non-Axios failure', {
        message: error instanceof Error ? error.message : String(error),
      });
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const body = error.response?.data;

    if (status === 401) {
      logApi('warn', `Unauthorized: ${requestLabel(error.config)}`, {
        httpStatus: status,
      });
      handleUnauthorized();
    } else if (!error.response) {
      logApi('error', `Network error: ${requestLabel(error.config)}`, {
        message: error.message,
      });
    } else if (isApiEnvelope(body)) {
      logApi('warn', `HTTP error: ${requestLabel(error.config)}`, {
        httpStatus: status,
        code: body.code,
        msg: body.msg,
        data: body.data,
      });
      if (body.msg) {
        error.message = body.msg;
      }
    } else {
      logApi('error', `HTTP error: ${requestLabel(error.config)}`, {
        httpStatus: status,
        data: body,
        message: error.message,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
